// DAS-2106 / DAS-2107: Portfolio Analysis View — Card grid layout
// Comprehensive analysis component that accepts portfolio data via props.
// Designed to work with both:
//   1. User's own portfolio (PortfolioModal tab, data from Redux)
//   2. Extracted portfolio data from file drops (/portfolio_analysis page)

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import getUserId from '../functions/getUserId';
import {
  Box, Grid, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, TextField, Button as MuiButton, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { message } from 'antd';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PieChartIcon from '@mui/icons-material/PieChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import StorageIcon from '@mui/icons-material/Storage';
import InsightsIcon from '@mui/icons-material/Insights';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EmojiNatureIcon from '@mui/icons-material/EmojiNature';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import SecurityIcon from '@mui/icons-material/Security';
import AnalysisCard from './AnalysisCard';
import TerminalLayout from './TerminalLayout';
import PAModal_Portfolio from './portfolio_analysis_modals/PAModal_Portfolio';
import PAModal_Exposures from './portfolio_analysis_modals/PAModal_Exposures';
import PAModal_Timeline from './portfolio_analysis_modals/PAModal_Timeline';
import PAModal_InstitutionalTrend from './portfolio_analysis_modals/PAModal_InstitutionalTrend';
import PAModal_Fundamentals from './portfolio_analysis_modals/PAModal_Fundamentals';
import PAModal_News from './portfolio_analysis_modals/PAModal_News';
import PAModal_ESG from './portfolio_analysis_modals/PAModal_ESG';
import PAModal_Alerts from './portfolio_analysis_modals/PAModal_Alerts';
import PAModal_Momentum from './portfolio_analysis_modals/PAModal_Momentum';
import PAModal_AssetAllocation from './portfolio_analysis_modals/PAModal_AssetAllocation';
import PAModal_Risk from './portfolio_analysis_modals/PAModal_Risk';
import PAModal_Performance from './portfolio_analysis_modals/PAModal_Performance';
import PAModal_NewsTrend from './portfolio_analysis_modals/PAModal_NewsTrend';
import PAModal_Income from './portfolio_analysis_modals/PAModal_Income';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import SavingsIcon from '@mui/icons-material/Savings';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import { fetchYFData, fetchTickerPerformance, fetchDividendData, fetchDepositInterest, fetchPortfolioRisk, fetchPortfolioRiskLive, fetchBenchmark } from '../helpers/portfolioAnalysisHelper';
import { TickerSignalContext } from './portfolio_analysis_modals/TickerSignalContext';
import api from '../functions/api';

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '—';
  return `€${Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const PortfolioAnalysisView = ({ data = [], source = 'portfolio', etfStockPositions = [], etfLeftoverPositions = [], fixedIncomeData }) => {
  const [openModal, setOpenModal] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('pa_viewMode') || 'cards');
  const [yfData, setYfData] = useState({});
  const [yfLoading, setYfLoading] = useState(false);
  const [tpData, setTpData] = useState({});
  const [tpLoading, setTpLoading] = useState(false);
  const [divData, setDivData] = useState({});
  const [divLoading, setDivLoading] = useState(false);
  const [depositInterestData, setDepositInterestData] = useState([]);
  const [portfolioRisk, setPortfolioRisk] = useState(null);
  const [portfolioRiskExposure, setPortfolioRiskExposure] = useState(null);
  const [portfolioRiskLoading, setPortfolioRiskLoading] = useState(false);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [signalAlerts, setSignalAlerts] = useState([]);
  const [tickerAggregates, setTickerAggregates] = useState([]);
  const [sectorSentiments, setSectorSentiments] = useState([]);
  const [tsLoading, setTsLoading] = useState(false);

  // DAS-2185: PDF download & Jira ticket creation state
  const [pdfMode, setPdfMode] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [jiraModalOpen, setJiraModalOpen] = useState(false);
  const [jiraDescription, setJiraDescription] = useState('');
  const [jiraContext, setJiraContext] = useState('');
  const [jiraLoading, setJiraLoading] = useState(false);
  const modalContentRef = useRef(null);
  const userId = getUserId();

  const reduxExposures = useSelector(state => state.portfolio?.data?.exposures || []);
  const reduxAlerts = useSelector(state => state.portfolio?.data?.pf_alert || []);

  const fileDropExposures = useMemo(() => {
    if (source !== 'file_drop' || !data.length) return [];

    const tickered = data.filter(d => d.Ticker);
    if (!tickered.length) return [];

    const totalEur = tickered.reduce((s, d) => s + (parseFloat(d.Value_eur) || 0), 0);
    const totalPct = tickered.reduce((s, d) => s + (parseFloat(d.exposure_pct) || 0), 0);
    const mode = totalEur > 0 ? 'eur' : totalPct > 0 ? 'pct' : 'equal';

    const getValue = (d) => {
      if (mode === 'eur') return parseFloat(d.Value_eur) || 0;
      if (mode === 'pct') return parseFloat(d.exposure_pct) || 0;
      return 1;
    };
    const denominator = mode === 'eur' ? totalEur
      : mode === 'pct' ? totalPct : tickered.length;

    const etfTickers = new Set(etfStockPositions.length > 0
      ? data.filter(d => d.top_holdings || d.etf_holdings).map(d => (d.Ticker || '').toUpperCase())
      : []);

    const agg = {};

    for (const d of tickered) {
      const key = d.Ticker.toUpperCase();
      if (etfTickers.has(key)) continue;

      const val = getValue(d);
      if (!agg[key]) {
        agg[key] = {
          ticker: d.Ticker,
          company: d.Company || '',
          Assettype: d.asset_type || '',
          stock_exposure: 0,
          tracker_exposure_amount: 0,
          tracker_details: [],
        };
      }
      agg[key].stock_exposure += val;
    }

    for (const sp of etfStockPositions) {
      const key = (sp.symbol || '').toUpperCase();
      if (!agg[key]) {
        agg[key] = {
          ticker: sp.symbol,
          company: sp.name || sp.symbol,
          Assettype: 'STOCK',
          stock_exposure: 0,
          tracker_exposure_amount: 0,
          tracker_details: [],
        };
      }
      agg[key].tracker_exposure_amount += sp.total_value || 0;
      for (const etf of (sp.etfs || [])) {
        agg[key].tracker_details.push(
          `${etf.ticker}:${(etf.value || 0).toFixed(2)} ${sp.currency || 'EUR'}`
        );
      }
    }

    for (const lp of etfLeftoverPositions) {
      const ticker = lp.ticker || lp.name;
      const key = (ticker || '').toUpperCase();
      const val = lp.leftover_value || 0;
      const lpName = (lp.name || '').toUpperCase();
      const lpType = (lpName.includes('ETF') || lpName.includes('UCITS') || lpName.includes('TRACKER'))
        ? 'ETF' : 'MUTUALFUND';
      agg[key] = {
        ticker,
        company: lp.name || ticker,
        Assettype: lpType,
        stock_exposure: 0,
        tracker_exposure_amount: 0,
        tracker_details: [],
        total_amount_exposure: val,
        total_amount_exposure_perc: denominator > 0 ? (val / denominator * 100) : 0,
        part_underlying_assets_unknown: true,
      };
    }

    return Object.values(agg).map(row => {
      const total = (row.total_amount_exposure != null)
        ? row.total_amount_exposure
        : row.stock_exposure + row.tracker_exposure_amount;
      return {
        ...row,
        total_amount_exposure: total,
        total_amount_exposure_perc: row.total_amount_exposure_perc != null
          ? row.total_amount_exposure_perc
          : (denominator > 0 ? (total / denominator * 100) : 0),
        tracker_exposure_amount: row.tracker_exposure_amount || null,
        tracker_exposure: row.tracker_details.length > 0 ? row.tracker_details.join(', ') : null,
        stock_exposure: row.stock_exposure || null,
      };
    }).sort((a, b) => b.total_amount_exposure - a.total_amount_exposure);
  }, [data, source, etfStockPositions, etfLeftoverPositions]);

  const exposures = source === 'file_drop' ? fileDropExposures : reduxExposures;

  const NON_STOCK_TYPES = new Set(['ETF', 'MUTUALFUND', 'BOND', 'CURRENCY', 'INDEX']);

  const tickers = useMemo(
    () => [...new Set(data.map(d => d.Ticker).filter(Boolean))].sort(),
    [data]
  );

  const stockTickers = useMemo(
    () => [...new Set(
      data
        .filter(d => {
          const t = (d.asset_type || d.Quotetype || '').toUpperCase();
          return d.Ticker && !NON_STOCK_TYPES.has(t);
        })
        .map(d => d.Ticker)
    )].sort(),
    [data]
  );

  const allTickers = useMemo(
    () => [...new Set([
      ...tickers,
      ...etfStockPositions.map(s => s.symbol).filter(Boolean),
    ])].sort(),
    [tickers, etfStockPositions]
  );

  const fetchKey = allTickers.join(',');
  const fetchedForRef = useRef('');
  const allTickersRef = useRef(allTickers);
  allTickersRef.current = allTickers;

  useEffect(() => {
    if (!fetchKey || fetchKey === fetchedForRef.current) return;
    fetchedForRef.current = fetchKey;
    const t = allTickersRef.current;

    setYfLoading(true);
    setTpLoading(true);
    setDivLoading(true);

    fetchYFData(t)
      .then(result => setYfData(result.yf_data || {}))
      .catch(err => console.error('[PortfolioAnalysisView] Failed to fetch YF data:', err))
      .finally(() => setYfLoading(false));

    fetchTickerPerformance(t)
      .then(result => setTpData(result.ticker_performance || {}))
      .catch(err => console.error('[PortfolioAnalysisView] Failed to fetch Ticker Performance data:', err))
      .finally(() => setTpLoading(false));

    fetchDividendData(t)
      .then(result => setDivData(result.dividend_data || {}))
      .catch(err => console.error('[PortfolioAnalysisView] Failed to fetch dividend data:', err))
      .finally(() => setDivLoading(false));

    const userId = getUserId();
    if (userId && source === 'portfolio') {
      fetchDepositInterest(userId)
        .then(result => setDepositInterestData(result.deposit_interest || []))
        .catch(err => console.error('[PortfolioAnalysisView] Failed to fetch deposit interest:', err));
    }

    fetchBenchmark('SPY')
      .then(result => setBenchmarkData(result.benchmark || null))
      .catch(err => console.error('[PortfolioAnalysisView] Failed to fetch benchmark:', err));

    api.post('/backtesting', {
      action: 'alerts_for_tickers',
      payload: { tickers: t, scopes: ['Unknown', 'Favorites', 'Portfolio'] }
    })
      .then(res => setSignalAlerts(res.data?.data?.alerts || []))
      .catch(err => console.error('[PortfolioAnalysisView] Failed to fetch signal alerts:', err));

    setTsLoading(true);
    Promise.all([
      api.get('/trendsearch_ticker_aggregates'),
      api.get('/trendsearch_sector_summary'),
    ])
      .then(([tickerRes, sectorRes]) => {
        if (tickerRes?.data?.success) setTickerAggregates(tickerRes.data.data || []);
        if (sectorRes?.data?.success) setSectorSentiments(sectorRes.data.data || []);
      })
      .catch(err => console.error('[PortfolioAnalysisView] Failed to fetch TrendSearch data:', err))
      .finally(() => setTsLoading(false));

    setPortfolioRiskLoading(true);
    if (source === 'portfolio') {
      fetchPortfolioRisk(t)
        .then(result => {
          setPortfolioRisk(result.portfolio_risk || null);
          setPortfolioRiskExposure(result.portfolio_risk_exposure || null);
        })
        .catch(err => console.error('[PortfolioAnalysisView] Failed to fetch portfolio risk:', err))
        .finally(() => setPortfolioRiskLoading(false));
    } else {
      const positions = data
        .filter(d => d.Ticker)
        .map(d => ({ ticker: d.Ticker, value: parseFloat(d.Value_eur) || parseFloat(d.exposure_pct) || 0 }));
      const exposurePositions = exposures
        .filter(e => e.ticker)
        .map(e => ({ ticker: e.ticker, value: parseFloat(e.total_amount_exposure) || 0 }));
      fetchPortfolioRiskLive(t, positions, exposurePositions)
        .then(result => {
          setPortfolioRisk(result.portfolio_risk || null);
          setPortfolioRiskExposure(result.portfolio_risk_exposure || null);
        })
        .catch(err => console.error('[PortfolioAnalysisView] Failed to compute live portfolio risk:', err))
        .finally(() => setPortfolioRiskLoading(false));
    }
  }, [fetchKey]);

  // --- Mini-stat computations ---

  // ─── dataProfile: shared data-quality metadata for all widgets ───
  const dataProfile = useMemo(() => {
    if (!data.length) {
      return {
        hasAbsoluteValues: false, hasExposurePct: false,
        valueSource: 'none', totalPortfolioValue: null,
        recordCount: 0,
      };
    }
    let eurCount = 0;
    let calcCount = 0;
    let expCount = 0;
    let eurTotal = 0;
    let expTotal = 0;

    for (const d of data) {
      const v = parseFloat(d.Value_eur);
      if (v > 0) { eurCount++; eurTotal += v; continue; }
      const qty = parseFloat(d.Position) || parseFloat(d.position_size) || 0;
      const price = parseFloat(d.purchase_cost) || parseFloat(d.latest_price) || 0;
      if (qty > 0 && price > 0) { calcCount++; eurTotal += qty * price; continue; }
      const exp = parseFloat(d.exposure_pct);
      if (exp > 0) { expCount++; expTotal += exp; }
    }

    const hasAbsoluteValues = (eurCount + calcCount) > 0;
    const hasExposurePct = expCount > 0;
    let valueSource = 'none';
    if (hasAbsoluteValues) valueSource = 'eur';
    else if (hasExposurePct) valueSource = 'exposure_pct';

    return {
      hasAbsoluteValues,
      hasExposurePct,
      valueSource,
      totalPortfolioValue: hasAbsoluteValues ? eurTotal : null,
      exposureTotal: hasExposurePct ? expTotal : null,
      recordCount: data.length,
    };
  }, [data]);

  const portfolioStats = useMemo(() => {
    if (!data.length) return [];
    const categories = [...new Set(data.map(item => item.Category).filter(Boolean))].length;
    if (dataProfile.hasAbsoluteValues) {
      return [
        { label: 'Value:', value: formatCurrency(dataProfile.totalPortfolioValue) },
        { label: 'Positions:', value: `${data.length} across ${categories} categories` },
      ];
    }
    if (dataProfile.hasExposurePct) {
      return [
        { label: 'Value:', value: 'Weights only' },
        { label: 'Positions:', value: `${data.length} across ${categories} categories` },
      ];
    }
    return [
      { label: 'Positions:', value: `${data.length} across ${categories} categories` },
    ];
  }, [data, dataProfile]);

  const exposureStats = useMemo(() => {
    if (!exposures.length) return [];
    const values = exposures
      .map(e => parseFloat(e.total_amount_exposure) || 0)
      .filter(v => v > 0)
      .sort((a, b) => b - a);
    const total = values.reduce((s, v) => s + v, 0);
    const weights = total > 0 ? values.map(v => v / total) : [];
    const hhi = Math.round(weights.reduce((s, w) => s + w * w, 0) * 10000);
    const hhiLabel = hhi < 1500 ? 'Diversified' : hhi < 2500 ? 'Moderate' : 'Concentrated';
    const hhiColor = hhi < 1500 ? '#2e7d32' : hhi < 2500 ? '#f57c00' : '#c62828';

    const sorted = [...exposures].sort(
      (a, b) => (parseFloat(b.total_amount_exposure) || 0) - (parseFloat(a.total_amount_exposure) || 0)
    );
    const top = sorted[0];
    const topPct = parseFloat(top.total_amount_exposure_perc);
    return [
      { label: 'Top:', value: `${top.ticker} (${!isNaN(topPct) ? topPct.toFixed(1) : '?'}%)` },
      { label: 'HHI:', value: `${hhi.toLocaleString()} ${hhiLabel}`, color: hhiColor },
    ];
  }, [exposures]);

  const timelineStats = useMemo(() => {
    if (!Object.keys(yfData).length && !Object.keys(divData).length && depositInterestData.length === 0) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcomingEarnings = Object.entries(yfData)
      .filter(([, yf]) => yf.next_earnings_date)
      .map(([ticker, yf]) => {
        const d = new Date(String(yf.next_earnings_date));
        const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
        return { ticker, diff, type: 'E' };
      })
      .filter(e => e.diff >= 0 && !isNaN(e.diff));

    const upcomingDivs = Object.entries(divData)
      .filter(([, dd]) => dd.next_estimated)
      .map(([ticker, dd]) => {
        const d = new Date(dd.next_estimated.ex_date);
        const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
        return { ticker, diff, type: 'D' };
      })
      .filter(e => e.diff >= 0 && !isNaN(e.diff));

    const upcomingInterest = depositInterestData
      .map(ev => {
        const d = new Date(ev.date);
        const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
        return { ticker: ev.label || 'Deposit', diff, type: 'I' };
      })
      .filter(e => e.diff >= 0 && !isNaN(e.diff));

    const allUpcoming = [...upcomingEarnings, ...upcomingDivs, ...upcomingInterest].sort((a, b) => a.diff - b.diff);

    if (allUpcoming.length === 0) return [{ label: 'Events:', value: 'None upcoming' }];
    const next = allUpcoming[0];
    const label = next.type === 'E' ? 'earnings' : next.type === 'D' ? 'ex-div' : 'interest';
    const parts = [`${upcomingEarnings.length} earnings`, `${upcomingDivs.length} dividends`];
    if (upcomingInterest.length > 0) parts.push(`${upcomingInterest.length} interest`);
    return [
      { label: 'Next:', value: `${next.ticker} ${label} in ${next.diff}d` },
      { label: 'Upcoming:', value: parts.join(' · ') },
    ];
  }, [yfData, divData, depositInterestData]);

  const fundamentalsStats = useMemo(() => {
    if (!Object.keys(tpData).length) return [];
    const scored = Object.entries(tpData)
      .map(([ticker, tp]) => ({ ticker, score: parseFloat(tp.fundamental_final_score) }))
      .filter(e => !isNaN(e.score))
      .sort((a, b) => b.score - a.score);
    if (scored.length === 0) return [];
    return [
      { label: 'Top:', value: `${scored[0].ticker} (${scored[0].score.toFixed(1)})` },
      { label: 'Scored:', value: `${scored.length} tickers` },
    ];
  }, [tpData]);

  const institutionalStats = useMemo(() => {
    if (!Object.keys(tpData).length) return [];
    const entries = Object.entries(tpData).filter(([, tp]) => tp.Likely_Institutional_Entry === 1);
    const exits = Object.entries(tpData).filter(([, tp]) => tp.Likely_Institutional_Exit === 1);
    return [
      { label: 'Entries:', value: `${entries.length}`, color: '#2e7d32' },
      { label: 'Exits:', value: `${exits.length}`, color: '#c62828' },
    ];
  }, [tpData]);

  const momentumStats = useMemo(() => {
    if (!Object.keys(tpData).length) return [];
    const entries = Object.values(tpData);
    const valid1M = entries.filter(tp => tp.one_month_performance != null);
    const avg1M = valid1M.length
      ? (valid1M.reduce((s, tp) => s + parseFloat(tp.one_month_performance), 0) / valid1M.length).toFixed(1)
      : null;
    const trendUp = entries.filter(tp => String(tp.price_trend_direction).toLowerCase().includes('up')).length;
    const total = entries.filter(tp => tp.price_trend_direction).length;
    const stats = [];
    if (avg1M != null) stats.push({ label: 'Avg 1M:', value: `${avg1M >= 0 ? '+' : ''}${avg1M}%`, color: avg1M >= 0 ? '#2e7d32' : '#c62828' });
    if (total > 0) stats.push({ label: 'Trend:', value: `${trendUp}/${total} up` });
    return stats;
  }, [tpData]);

  const esgStats = useMemo(() => {
    if (!tickers.length) return [];
    return [
      { label: 'Tickers:', value: `${stockTickers.length} stocks` },
      { label: 'Providers:', value: 'SA · MSCI · Refinitiv' },
    ];
  }, [tickers, stockTickers]);

  const riskStats = useMemo(() => {
    const stats = [];
    if (portfolioRisk) {
      // DAS-2746: prefer the all-parts blend (same definition as the Fair-comparison
      // Overall — every sleeve incl. W&O, size-weighted) so the tile and the Fair
      // comparison show ONE consistent risk number. Falls back to the COMBINED
      // (market-holdings-only) figure, explicitly labelled, on older payloads.
      const allVol = portfolioRisk.overall_vol_all_parts;
      const vol = allVol != null ? allVol : portfolioRisk.annualized_volatility;
      // DAS-2763: stream-aware labels — the logged-in stream (source 'portfolio') has
      // account history; a dropped FILE has none, so its figures are holdings-based.
      const volLabel = allVol != null ? 'Vol (all parts):'
        : source === 'portfolio' ? 'Vol (market only):' : 'Vol (holdings 1y):';
      if (vol != null) stats.push({ label: volLabel, value: `${Number(vol).toFixed(1)}%`, color: vol > 25 ? '#c62828' : vol > 15 ? '#f57c00' : '#2e7d32' });
      // DAS-2763: prefer the Sharpe built from ACTUAL account returns; the fallback
      // (file-drop, or history unavailable) is the current-holdings-held-all-year
      // figure — a hypothetical, and labelled as such.
      const allSharpe = portfolioRisk.overall_sharpe_all_parts;
      const sharpe = allSharpe != null ? allSharpe : portfolioRisk.portfolio_sharpe;
      const sharpeLabel = allSharpe != null ? 'Sharpe (real 1y):' : 'Sharpe (hyp.):';
      if (sharpe != null) stats.push({ label: sharpeLabel, value: Number(sharpe).toFixed(2), color: sharpe >= 1 ? '#2e7d32' : sharpe >= 0.5 ? '#f57c00' : '#c62828' });
    } else if (Object.keys(tpData).length || Object.keys(yfData).length) {
      const betaEntries = Object.values(yfData).map(yf => parseFloat(yf.beta)).filter(b => !isNaN(b));
      const probEntries = Object.values(tpData).map(tp => parseFloat(tp.return_probability)).filter(p => !isNaN(p));
      if (betaEntries.length > 0) {
        const avgBeta = (betaEntries.reduce((s, v) => s + v, 0) / betaEntries.length).toFixed(2);
        stats.push({ label: 'Avg Beta:', value: avgBeta, color: avgBeta > 1.2 ? '#f57c00' : avgBeta < 0.8 ? '#2e7d32' : undefined });
      }
      if (probEntries.length > 0) {
        const avgProb = Math.round(probEntries.reduce((s, v) => s + v, 0) / probEntries.length);
        stats.push({ label: 'P(Ret+):', value: `${avgProb}%`, color: avgProb >= 60 ? '#2e7d32' : avgProb >= 50 ? '#f57c00' : '#c62828' });
      }
    }
    return stats;
  }, [tpData, yfData, portfolioRisk]);

  const performanceStats = useMemo(() => {
    if (!Object.keys(tpData).length || !data.length) return [];
    const totalVal = data.reduce((s, d) => s + (parseFloat(d.Value_eur) || 0), 0);
    const useWeights = totalVal > 0;
    let wSum = 0;
    let wRet = 0;
    let topTicker = null;
    let topContrib = -Infinity;
    for (const d of data) {
      if (!d.Ticker) continue;
      const tp = tpData[d.Ticker.toUpperCase()] || {};
      const ret = parseFloat(tp.one_month_performance);
      if (isNaN(ret)) continue;
      const v = parseFloat(d.Value_eur) || 0;
      const w = useWeights ? (totalVal > 0 ? v / totalVal : 0) : (1 / data.length);
      wSum += w;
      wRet += w * ret;
      const contrib = w * ret;
      if (contrib > topContrib) { topContrib = contrib; topTicker = d.Ticker; }
    }
    const stats = [];
    if (wSum > 0) {
      const pRet = wRet / wSum;
      stats.push({ label: '1M:', value: `${pRet >= 0 ? '+' : ''}${pRet.toFixed(1)}%`, color: pRet >= 0 ? '#2e7d32' : '#c62828' });
    }
    if (topTicker) stats.push({ label: 'Top:', value: topTicker });
    return stats;
  }, [tpData, data]);

  // DAS-2173: Income card stats — uses same yield waterfall as FIP
  const incomeStats = useMemo(() => {
    if (!data.length) return [];
    let totalIncome = 0;
    let sourceCount = 0;

    const depositOiSet = new Set(
      depositInterestData.filter(ev => ev.oi_id).map(ev => `OI_${ev.oi_id}`.toUpperCase())
    );

    for (const d of data) {
      if (!d.Ticker) continue;
      if (depositOiSet.has(d.Ticker.toUpperCase())) continue;
      const yf = yfData[d.Ticker.toUpperCase()] || {};
      const posVal = parseFloat(d.Value_eur) || 0;
      if (posVal <= 0) continue;

      const manPct = parseFloat(d.manual_percentage);
      const coupPct = parseFloat(d.coupon_percentage);
      const bankPct = parseFloat(d.bank_interest_percentage);
      const yfDiv = parseFloat(yf.Dividend);
      const divPct = parseFloat(d.dividend_percentage);
      const rate = manPct > 0 ? manPct : coupPct > 0 ? coupPct : bankPct > 0 ? bankPct : yfDiv > 0 ? yfDiv : divPct > 0 ? divPct : 0;

      if (rate > 0) {
        totalIncome += posVal * (rate / 100);
        sourceCount++;
      }
    }

    for (const ev of depositInterestData) {
      const principal = ev.principal || 0;
      const rate = ev.rate || 0;
      if (principal > 0 && rate > 0) {
        totalIncome += principal * (rate / 100);
        sourceCount++;
      }
    }

    if (totalIncome <= 0) return [];
    return [
      { label: 'Annual:', value: `€${Math.round(totalIncome).toLocaleString()}` },
      { label: 'Sources:', value: `${sourceCount} positions` },
    ];
  }, [data, yfData, depositInterestData]);

  const newsTrendStats = useMemo(() => {
    if (!tickerAggregates.length || !data.length) return [];
    const aggMap = Object.create(null);
    tickerAggregates.forEach(row => { aggMap[(row.ticker || '').toUpperCase()] = row; });

    const src = exposures.length > 0 ? exposures : data;
    let totalValue = 0, winnerValue = 0, loserValue = 0, matchCount = 0;

    src.forEach(h => {
      const ticker = (h.ticker || h.Ticker || '').toUpperCase();
      const val = parseFloat(h.total_amount_exposure || h.Value_eur || h.exposure_pct || 0);
      totalValue += val;
      const agg = aggMap[ticker];
      if (!agg) return;
      matchCount++;
      const w = parseInt(agg.winner_count || 0, 10);
      const l = parseInt(agg.loser_count || 0, 10);
      const e = parseInt(agg.enabler_count || 0, 10);
      if (w > l && w >= e) winnerValue += val;
      else if (l > w && l >= e) loserValue += val;
    });

    if (matchCount === 0) return [];
    const pct = (x) => totalValue > 0 ? (x / totalValue * 100).toFixed(1) : '0.0';
    return [
      { label: 'Winners:', value: `${pct(winnerValue)}%`, color: '#2e7d32' },
      { label: 'Losers:', value: `${pct(loserValue)}%`, color: '#c62828' },
    ];
  }, [tickerAggregates, data, exposures]);

  const placeholderStats = useMemo(() => {
    return [{ label: 'Status:', value: 'Coming soon', color: '#999' }];
  }, []);

  // ─── DAS-2181: Terminal hero data for each card ───
  const terminalHeroData = useMemo(() => {
    const categories = [...new Set(data.map(d => d.Category).filter(Boolean))].length;

    // Portfolio hero
    const portfolioHero = {
      label: 'portfolio value',
      value: dataProfile.hasAbsoluteValues
        ? formatCurrency(dataProfile.totalPortfolioValue)
        : dataProfile.hasExposurePct ? 'Weights only' : '\u2014',
      valueClass: 'big',
      sub: data.length > 0 ? `${data.length} positions \u00B7 ${categories} categories` : null,
      allocationBar: (() => {
        if (!exposures.length) return null;
        const sorted = [...exposures].sort(
          (a, b) => (parseFloat(b.total_amount_exposure) || 0) - (parseFloat(a.total_amount_exposure) || 0)
        );
        const total = sorted.reduce((s, e) => s + (parseFloat(e.total_amount_exposure) || 0), 0);
        if (total <= 0) return null;
        const colors = ['#4a90c4', '#3daa6e', '#c8852a', '#7a6ab0', '#2a3040'];
        const top5 = sorted.slice(0, 5);
        return top5.map((e, i) => ({
          flex: (parseFloat(e.total_amount_exposure) || 0) / total * 100,
          color: colors[i] || '#2a3040',
        }));
      })(),
    };

    // Income hero
    let totalIncome = 0;
    let incSourceCount = 0;
    let divIncome = 0;
    let fixedIncome = 0;
    let interestIncome = 0;
    const depositOiSet = new Set(
      depositInterestData.filter(ev => ev.oi_id).map(ev => `OI_${ev.oi_id}`.toUpperCase())
    );
    for (const d of data) {
      if (!d.Ticker) continue;
      if (depositOiSet.has(d.Ticker.toUpperCase())) continue;
      const yf = yfData[d.Ticker.toUpperCase()] || {};
      const posVal = parseFloat(d.Value_eur) || 0;
      if (posVal <= 0) continue;
      const manPct = parseFloat(d.manual_percentage);
      const coupPct = parseFloat(d.coupon_percentage);
      const bankPct = parseFloat(d.bank_interest_percentage);
      const yfDiv = parseFloat(yf.Dividend);
      const divPct = parseFloat(d.dividend_percentage);
      const rate = manPct > 0 ? manPct : coupPct > 0 ? coupPct : bankPct > 0 ? bankPct : yfDiv > 0 ? yfDiv : divPct > 0 ? divPct : 0;
      if (rate > 0) {
        const inc = posVal * (rate / 100);
        totalIncome += inc;
        incSourceCount++;
        if (coupPct > 0) fixedIncome += inc;
        else if (bankPct > 0) interestIncome += inc;
        else divIncome += inc;
      }
    }
    for (const ev of depositInterestData) {
      const principal = ev.principal || 0;
      const rate = ev.rate || 0;
      if (principal > 0 && rate > 0) {
        const inc = principal * (rate / 100);
        totalIncome += inc;
        incSourceCount++;
        interestIncome += inc;
      }
    }
    const portfolioTotal = dataProfile.totalPortfolioValue || 0;
    const yieldPct = portfolioTotal > 0 ? (totalIncome / portfolioTotal * 100).toFixed(2) : '0';
    const fmtK = (v) => v >= 1000 ? `\u20AC${(v / 1000).toFixed(1)}K` : `\u20AC${Math.round(v)}`;
    const incomeHero = {
      label: 'annual income',
      value: totalIncome > 0 ? `\u20AC${Math.round(totalIncome).toLocaleString()}` : '\u2014',
      sub: totalIncome > 0 ? `${incSourceCount} sources \u00B7 ${yieldPct}% yield` : null,
      sub2: totalIncome > 0
        ? `div ${fmtK(divIncome)} \u00B7 fixed ${fmtK(fixedIncome)} \u00B7 int ${fmtK(interestIncome)}`
        : null,
    };

    // Performance hero
    let wSum1M = 0, wRet1M = 0, topPerf = null;
    const totalVal = data.reduce((s, d) => s + (parseFloat(d.Value_eur) || 0), 0);
    for (const d of data) {
      if (!d.Ticker) continue;
      const tp = tpData[d.Ticker.toUpperCase()] || {};
      const ret = parseFloat(tp.one_month_performance);
      if (isNaN(ret)) continue;
      const v = parseFloat(d.Value_eur) || 0;
      const w = totalVal > 0 ? v / totalVal : 1 / data.length;
      wSum1M += w;
      wRet1M += w * ret;
      if (!topPerf || w * ret > topPerf.contrib) topPerf = { ticker: d.Ticker, contrib: w * ret };
    }
    const pRet1M = wSum1M > 0 ? wRet1M / wSum1M : null;
    const sharpe = portfolioRisk?.portfolio_sharpe;

    let wSumYTD = 0, wRetYTD = 0;
    for (const d of data) {
      if (!d.Ticker) continue;
      const tp = tpData[d.Ticker.toUpperCase()] || {};
      const ret = parseFloat(tp.YTD);
      if (isNaN(ret)) continue;
      const v = parseFloat(d.Value_eur) || 0;
      const w = totalVal > 0 ? v / totalVal : 1 / data.length;
      wSumYTD += w;
      wRetYTD += w * ret;
    }
    const pRetYTD = wSumYTD > 0 ? wRetYTD / wSumYTD : null;
    const benchYtd = benchmarkData?.returns?.YTD ?? null;

    const performanceHero = {
      label: 'performance',
      inlineGroup: [
        {
          value: pRet1M != null ? `${pRet1M >= 0 ? '+' : ''}${pRet1M.toFixed(1)}%` : '\u2014',
          colorClass: pRet1M != null ? (pRet1M >= 0 ? 'up' : 'dn') : 'neu',
          sub: '1M',
        },
        {
          value: pRetYTD != null ? `${pRetYTD >= 0 ? '+' : ''}${pRetYTD.toFixed(1)}%` : '\u2014',
          colorClass: pRetYTD != null ? (pRetYTD >= 0 ? 'up' : 'dn') : 'neu',
          sub: 'YTD',
        },
      ],
      sub: [
        sharpe != null ? `sharpe ${Number(sharpe).toFixed(2)}` : null,
        benchYtd != null ? `SPY ${benchYtd >= 0 ? '+' : ''}${Number(benchYtd).toFixed(1)}%` : null,
        topPerf ? `top: ${topPerf.ticker}` : null,
      ].filter(Boolean).join(' \u00B7 ') || null,
    };

    // Momentum hero
    const tpVals = Object.values(tpData);
    const valid1M = tpVals.filter(tp => tp.one_month_performance != null);
    const avg1M = valid1M.length
      ? valid1M.reduce((s, tp) => s + parseFloat(tp.one_month_performance), 0) / valid1M.length
      : null;
    const trendUp = tpVals.filter(tp => String(tp.price_trend_direction).toLowerCase().includes('up')).length;
    const trendTotal = tpVals.filter(tp => tp.price_trend_direction).length;
    const momentumHero = {
      label: 'momentum',
      value: avg1M != null ? `${avg1M >= 0 ? '+' : ''}${avg1M.toFixed(1)}%` : '\u2014',
      colorClass: avg1M != null ? (avg1M >= 0 ? 'up' : 'dn') : 'neu',
      sub: trendTotal > 0 ? `${trendUp} / ${trendTotal} trending up` : null,
      momentumBar: trendTotal > 0 ? { up: trendUp, rest: trendTotal - trendUp } : null,
    };

    // Cash hero — mirrors classifyHolding() from PAModal_AssetAllocation
    const CASH_DIRECT = { Cash: 1, CASH: 1, Savings: 1, Deposits: 1, 'Money Market': 1 };
    const CASH_RE_HERO = /\bcash\b|money.?market|savings|deposit|liquidity|liquiditeiten|sweep/i;
    const cashPositions = data.filter(d => {
      const at = (d.asset_type || d.Quotetype || '').trim();
      if (CASH_DIRECT[at]) return true;
      if (at.toUpperCase() === 'CURRENCY') return true;
      const name = d.Company || d.Ticker || '';
      if (CASH_RE_HERO.test(name)) return true;
      const cat = (d.Category || '').toLowerCase();
      return cat === 'cash' || cat === 'liquidity';
    });
    const cashValue = cashPositions.reduce((s, d) => s + (parseFloat(d.Value_eur) || 0), 0);
    const cashPct = portfolioTotal > 0 ? (cashValue / portfolioTotal * 100).toFixed(1) : '0';
    const cashHero = {
      label: 'cash position',
      value: cashValue > 0 ? formatCurrency(cashValue) : '\u2014',
      colorClass: 'neu',
      sub: cashValue > 0 ? `${cashPct}% of portfolio` : null,
    };

    // Risk/Regime hero
    const vol = portfolioRisk?.annualized_volatility;
    const hhiValues = exposures
      .map(e => parseFloat(e.total_amount_exposure) || 0)
      .filter(v => v > 0);
    const hhiTotal = hhiValues.reduce((s, v) => s + v, 0);
    const hhiWeights = hhiTotal > 0 ? hhiValues.map(v => v / hhiTotal) : [];
    const hhi = Math.round(hhiWeights.reduce((s, w) => s + w * w, 0) * 10000);
    let regime = 'NEUTRAL';
    let regimeClass = 'warn';
    if (vol != null) {
      if (vol < 12) { regime = 'LOW VOL'; regimeClass = 'up'; }
      else if (vol > 25) { regime = 'HIGH VOL'; regimeClass = 'dn'; }
    }
    const riskHero = {
      label: 'regime',
      value: regime,
      colorClass: regimeClass,
      sub: [
        vol != null ? `vol ${Number(vol).toFixed(1)}%` : null,
        hhi > 0 ? `HHI ${hhi.toLocaleString()}` : null,
      ].filter(Boolean).join(' \u00B7 ') || null,
    };

    return { portfolioHero, incomeHero, performanceHero, momentumHero, cashHero, riskHero };
  }, [data, dataProfile, exposures, yfData, tpData, depositInterestData, portfolioRisk, benchmarkData]);

  // ─── DAS-2181: Terminal panel data (Phase 2) ───
  const terminalPanelData = useMemo(() => {
    const fmtCur = (v) => v >= 0 ? `+€${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : `-€${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

    // --- Panel A: Risk & Allocation ---
    const vol = portfolioRisk?.annualized_volatility;
    const sharpe = portfolioRisk?.portfolio_sharpe;
    const portfolioTotal = dataProfile.totalPortfolioValue || 0;
    const sortedExposures = [...exposures].sort((a, b) =>
      Math.abs(parseFloat(b.total_amount_exposure) || 0) - Math.abs(parseFloat(a.total_amount_exposure) || 0)
    );
    const topExp = sortedExposures[0];
    const topExpPct = topExp && portfolioTotal > 0
      ? ((parseFloat(topExp.total_amount_exposure) || 0) / portfolioTotal * 100).toFixed(1)
      : null;

    const hhiValues = exposures.map(e => parseFloat(e.total_amount_exposure) || 0).filter(v => v > 0);
    const hhiTotal = hhiValues.reduce((s, v) => s + v, 0);
    const hhiWeights = hhiTotal > 0 ? hhiValues.map(v => v / hhiTotal) : [];
    const hhi = Math.round(hhiWeights.reduce((s, w) => s + w * w, 0) * 10000);
    let hhiLabel = 'well diversified';
    let hhiClass = 'up';
    if (hhi > 2500) { hhiLabel = 'concentrated'; hhiClass = 'dn'; }
    else if (hhi > 1500) { hhiLabel = 'moderate'; hhiClass = 'warn'; }

    let volClass = 'neu';
    if (vol != null) {
      if (vol < 12) volClass = 'up';
      else if (vol > 20) volClass = 'dn';
      else volClass = 'warn';
    }

    const assetClassSet = new Set();
    const AC_DIRECT = { Stock: 'Equity', STOCK: 'Equity', Equity: 'Equity', Bond: 'Fixed Income', BOND: 'Fixed Income', Cash: 'Cash', CASH: 'Cash', Savings: 'Cash', Cryptocurrency: 'Crypto', CRYPTOCURRENCY: 'Crypto' };
    const AC_CASH_RE = /\bcash\b|money.?market|savings|deposit|liquidity/i;
    const AC_FI_RE = /bond|treasury|fixed.?income|aggregate.?bond/i;
    const AC_REAL_RE = /real.?estate|reit|commodity|gold|silver|infrastructure/i;
    const AC_CRYPTO_RE = /crypto|bitcoin|ethereum|blockchain/i;
    for (const d of data) {
      const at = (d.asset_type || d.Quotetype || '').trim();
      if (AC_DIRECT[at]) { assetClassSet.add(AC_DIRECT[at]); continue; }
      const name = d.Company || d.Ticker || '';
      if (AC_CASH_RE.test(name)) assetClassSet.add('Cash');
      else if (AC_FI_RE.test(name)) assetClassSet.add('Fixed Income');
      else if (AC_REAL_RE.test(name)) assetClassSet.add('Real Assets');
      else if (AC_CRYPTO_RE.test(name)) assetClassSet.add('Crypto');
      else if (['ETF', 'MUTUALFUND'].includes(at.toUpperCase())) assetClassSet.add('Equity');
      else assetClassSet.add('Equity');
    }

    let riskBadge = { text: 'ok', cls: 'ok' };
    if (vol != null && vol > 20) riskBadge = { text: 'review', cls: 'warn' };
    if (hhi > 2500) riskBadge = { text: 'concentrated', cls: 'alert' };

    // Top 3 and top 5 exposures for concentration insight
    const top3Pct = sortedExposures.slice(0, 3).reduce((s, e) =>
      s + (portfolioTotal > 0 ? (parseFloat(e.total_amount_exposure) || 0) / portfolioTotal * 100 : 0), 0);
    const top5Pct = sortedExposures.slice(0, 5).reduce((s, e) =>
      s + (portfolioTotal > 0 ? (parseFloat(e.total_amount_exposure) || 0) / portfolioTotal * 100 : 0), 0);
    const divRatio = portfolioRisk?.diversification_ratio;

    const riskPanel = {
      badge: riskBadge,
      rows: [
        { key: 'volatility (ann.)', value: vol != null ? `${vol.toFixed(1)}%` : '—', cls: volClass },
        { key: 'sharpe ratio', value: sharpe != null ? Number(sharpe).toFixed(2) : '—', cls: sharpe != null ? (sharpe >= 1 ? 'up' : sharpe >= 0.5 ? 'neu' : 'dn') : '' },
        { key: 'top exposure', value: topExp ? `${topExp.ticker}  ${topExpPct}%` : '—' },
        { key: 'HHI concentration', value: hhi > 0 ? `${hhi.toLocaleString()} ${hhiLabel}` : '—', cls: hhiClass },
        { divider: true },
        { key: 'top-3 weight', value: top3Pct > 0 ? `${top3Pct.toFixed(1)}%` : '—', cls: top3Pct > 50 ? 'warn' : 'neu' },
        { key: 'top-5 weight', value: top5Pct > 0 ? `${top5Pct.toFixed(1)}%` : '—', cls: top5Pct > 60 ? 'warn' : 'neu' },
        { key: 'diversification', value: divRatio != null ? `${divRatio.toFixed(2)}x` : '—', cls: divRatio != null ? (divRatio >= 1.5 ? 'up' : divRatio >= 1.2 ? 'neu' : 'dn') : '' },
      ],
    };

    // --- Panel B: Fundamentals & Signals ---
    const scored = Object.entries(tpData)
      .map(([ticker, tp]) => ({ ticker, score: parseFloat(tp.fundamental_final_score) }))
      .filter(e => !isNaN(e.score))
      .sort((a, b) => b.score - a.score);
    const instEntries = Object.values(tpData).filter(tp => tp.Likely_Institutional_Entry === 1).length;
    const instExits = Object.values(tpData).filter(tp => tp.Likely_Institutional_Exit === 1).length;

    const aggMap = Object.create(null);
    tickerAggregates.forEach(row => { aggMap[(row.ticker || '').toUpperCase()] = row; });
    const tsrc = exposures.length > 0 ? exposures : data;
    let tsTotalValue = 0, tsWinnerValue = 0, tsLoserValue = 0;
    tsrc.forEach(h => {
      const ticker = (h.ticker || h.Ticker || '').toUpperCase();
      const val = parseFloat(h.total_amount_exposure || h.Value_eur || 0);
      tsTotalValue += val;
      const agg = aggMap[ticker];
      if (!agg) return;
      const w = parseInt(agg.winner_count || 0, 10);
      const l = parseInt(agg.loser_count || 0, 10);
      const e = parseInt(agg.enabler_count || 0, 10);
      if (w > l && w >= e) tsWinnerValue += val;
      else if (l > w && l >= e) tsLoserValue += val;
    });
    const tsPct = (x) => tsTotalValue > 0 ? `${(x / tsTotalValue * 100).toFixed(1)}%` : '—';

    // Compute portfolio weight in positive vs negative sentiment sectors
    // Sector comes from yfData per ticker, sentiment map uses exact sector names (matching PF_Analysis)
    const sectorSentimentMap = {};
    sectorSentiments.forEach(s => {
      const name = (s.sector || '').trim();
      if (name) sectorSentimentMap[name] = parseFloat(s.overall_sentiment || 0);
    });
    let positiveSectorWeight = 0, negativeSectorWeight = 0;
    const expSrc = exposures.length > 0 ? exposures : data;
    expSrc.forEach(h => {
      const ticker = (h.ticker || h.Ticker || '').toUpperCase();
      const yf = yfData[ticker];
      const sector = (yf?.sector || '').trim();
      const val = Math.abs(parseFloat(h.total_amount_exposure || h.Value_eur || 0));
      const sent = sectorSentimentMap[sector];
      if (sent != null && sent >= 2) positiveSectorWeight += val;
      else if (sent != null && sent <= -2) negativeSectorWeight += val;
    });
    const posSectorPct = portfolioTotal > 0 ? (positiveSectorWeight / portfolioTotal * 100).toFixed(1) : '0.0';
    const negSectorPct = portfolioTotal > 0 ? (negativeSectorWeight / portfolioTotal * 100).toFixed(1) : '0.0';

    const fundsBadge = scored.length > 0
      ? { text: `${scored.length} scored`, cls: 'ok' }
      : { text: 'loading', cls: 'info' };

    const fundamentalsPanel = {
      badge: fundsBadge,
      rows: [
        { key: 'top score', value: scored.length > 0 ? `${scored[0].ticker}  ${scored[0].score.toFixed(1)}` : '—', cls: scored.length > 0 ? 'up' : '' },
        { key: 'inst. entries', value: `${instEntries}`, cls: instEntries > 0 ? 'up' : 'neu' },
        { key: 'inst. exits', value: `${instExits}`, cls: instExits > 0 ? 'dn' : 'neu' },
        { divider: true },
        { sectionLabel: 'trendsearch exposure' },
        { key: 'winners (portfolio)', value: tsPct(tsWinnerValue), cls: 'up' },
        { key: 'losers (portfolio)', value: tsPct(tsLoserValue), cls: tsLoserValue > 0 ? 'dn' : 'neu' },
        { key: 'positive sectors', value: `${posSectorPct}% of portfolio`, cls: 'up' },
        { key: 'negative sectors', value: `${negSectorPct}% of portfolio`, cls: parseFloat(negSectorPct) > 20 ? 'dn' : 'neu' },
      ],
    };

    // --- Panel C: Timeline & Alerts ---
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const upcomingEarnings = Object.entries(yfData)
      .filter(([, yf]) => yf.next_earnings_date)
      .map(([ticker, yf]) => {
        const d = new Date(String(yf.next_earnings_date));
        return { ticker, diff: Math.round((d - today) / 86400000), type: 'E' };
      })
      .filter(e => e.diff >= 0 && !isNaN(e.diff));
    const upcomingDivs = Object.entries(divData)
      .filter(([, dd]) => dd.next_estimated)
      .map(([ticker, dd]) => {
        const d = new Date(dd.next_estimated.ex_date);
        return { ticker, diff: Math.round((d - today) / 86400000), type: 'D' };
      })
      .filter(e => e.diff >= 0 && !isNaN(e.diff));
    const upcomingInterest = depositInterestData
      .map(ev => ({ ticker: ev.label || 'Deposit', diff: Math.round((new Date(ev.date) - today) / 86400000), type: 'I' }))
      .filter(e => e.diff >= 0 && !isNaN(e.diff));

    const allUpcoming = [...upcomingEarnings, ...upcomingDivs, ...upcomingInterest].sort((a, b) => a.diff - b.diff);
    const nextEvent = allUpcoming[0];
    const nextLabel = nextEvent ? (nextEvent.type === 'E' ? 'earnings' : nextEvent.type === 'D' ? 'ex-div' : 'interest') : null;

    const activeAlerts = source === 'portfolio'
      ? reduxAlerts.filter(a => !a.bignore).length
      : signalAlerts.filter(a => !a.bignore).length;

    const alertBadge = activeAlerts > 0
      ? { text: `${activeAlerts} alerts`, cls: 'alert' }
      : { text: 'clear', cls: 'ok' };

    // Next 3 events for a richer timeline view
    const next3 = allUpcoming.slice(0, 3);
    const evLabel = (e) => e.type === 'E' ? 'earnings' : e.type === 'D' ? 'ex-div' : 'interest';

    // Earnings this week / next week
    const earningsThisWeek = upcomingEarnings.filter(e => e.diff <= 7).length;
    const divsThisWeek = upcomingDivs.filter(e => e.diff <= 7).length;

    // Alert breakdown by type
    const allAlerts = source === 'portfolio'
      ? reduxAlerts.filter(a => !a.bignore)
      : signalAlerts.filter(a => !a.bignore);
    const harvestAlerts = allAlerts.filter(a => (a.alert_type || '').toLowerCase() === 'harvest').length;
    const accumAlerts = allAlerts.filter(a => (a.alert_type || '').toLowerCase() === 'accumulate').length;

    const timelinePanel = {
      badge: alertBadge,
      rows: [
        { key: 'next event', value: nextEvent ? `${nextEvent.ticker} ${evLabel(nextEvent)} in ${nextEvent.diff}d` : 'none upcoming', cls: nextEvent && nextEvent.diff <= 3 ? 'warn' : nextEvent && nextEvent.diff <= 7 ? 'neu' : '' },
        { key: 'earnings this week', value: `${earningsThisWeek}`, cls: earningsThisWeek > 0 ? 'warn' : 'neu' },
        { key: 'ex-divs this week', value: `${divsThisWeek}`, cls: divsThisWeek > 0 ? 'warn' : 'neu' },
        { divider: true },
        { sectionLabel: 'alert summary' },
        ...(harvestAlerts > 0 ? [{ key: 'harvest signals', value: `${harvestAlerts}`, cls: 'dn' }] : []),
        ...(accumAlerts > 0 ? [{ key: 'accumulate signals', value: `${accumAlerts}`, cls: 'up' }] : []),
        ...(harvestAlerts === 0 && accumAlerts === 0 ? [{ key: 'signals', value: 'none', cls: 'neu' }] : []),
      ],
    };

    return { riskPanel, fundamentalsPanel, timelinePanel };
  }, [data, dataProfile, exposures, portfolioRisk, tpData, yfData, divData, depositInterestData,
      tickerAggregates, sectorSentiments, signalAlerts, reduxAlerts, stockTickers, source]);

  const tickerSignals = useMemo(() => {
    const signals = {};
    const alerts = signalAlerts.filter(a => !a.bignore);

    for (const ticker of allTickers) {
      const key = ticker.toUpperCase();
      const greenReasons = [];
      const redReasons = [];

      const tp = tpData[key];
      if (tp) {
        if (tp.Likely_Institutional_Entry === 1)
          greenReasons.push(tp.institutional_trend_text || 'Likely Institutional Entry');
        if (tp.Likely_Institutional_Exit === 1)
          redReasons.push(tp.institutional_trend_text || 'Likely Institutional Exit');
      }

      const tickerAlerts = alerts.filter(a => (a.ticker || '').toUpperCase() === key);
      for (const alert of tickerAlerts) {
        const scope = (alert.scope || '').toLowerCase();
        const alertType = (alert.alert_type || '').toLowerCase();
        const text = alert.pf_alert_text || alert.alert_type || 'Alert';

        if (scope === 'unknown' || scope === 'favorites')
          greenReasons.push(text);
        else if (scope === 'portfolio' && alertType === 'accumulate')
          greenReasons.push(text);
        else if (scope === 'portfolio' && alertType === 'harvest')
          redReasons.push(text);
      }

      if (greenReasons.length > 0 || redReasons.length > 0) {
        signals[key] = {
          hasGreen: greenReasons.length > 0,
          hasRed: redReasons.length > 0,
          greenReasons,
          redReasons,
        };
      }
    }

    return signals;
  }, [allTickers, tpData, signalAlerts]);

  const cards = [
    {
      id: 'portfolio',
      title: 'Your Portfolio',
      icon: <AccountBalanceWalletIcon />,
      color: '#1976d2',
      stats: portfolioStats,
      disabled: false,
      terminal: { hero: terminalHeroData.portfolioHero },
    },
    {
      id: 'exposures',
      title: 'Your Exposures',
      icon: <PieChartIcon />,
      color: '#7b1fa2',
      stats: exposureStats,
      disabled: exposures.length === 0,
    },
    {
      id: 'asset_allocation',
      title: 'Your Asset Allocation',
      icon: <DonutLargeIcon />,
      color: '#00897b',
      stats: data.length > 0
        ? [{ label: 'Classes:', value: '6 + unclassified' }, { label: 'Positions:', value: `${data.length}` }]
        : [],
      disabled: data.length === 0,
    },
    {
      id: 'income',
      title: 'Your Income',
      icon: <SavingsIcon />,
      color: '#e65100',
      stats: incomeStats,
      loading: (divLoading || yfLoading) && incomeStats.length === 0,
      disabled: data.length === 0 && depositInterestData.length === 0,
      terminal: { hero: terminalHeroData.incomeHero },
    },
    {
      id: 'timeline',
      title: 'Your Timeline',
      icon: <TimelineIcon />,
      color: '#0097a7',
      stats: timelineStats,
      loading: (yfLoading || divLoading) && timelineStats.length === 0,
      disabled: Object.keys(yfData).length === 0 && Object.keys(divData).length === 0 && !yfLoading && !divLoading,
    },
    {
      id: 'datamanagement',
      title: 'Your Datamanagement',
      icon: <StorageIcon />,
      color: '#455a64',
      stats: placeholderStats,
      disabled: true,
    },
    {
      id: 'fundamentals',
      title: 'Your Fundamentals',
      icon: <InsightsIcon />,
      color: '#2e7d32',
      stats: fundamentalsStats,
      loading: tpLoading && fundamentalsStats.length === 0,
      disabled: Object.keys(tpData).length === 0 && !tpLoading,
    },
    {
      id: 'news',
      title: 'Your News',
      icon: <NewspaperIcon />,
      color: '#e65100',
      stats: stockTickers.length > 0 ? [{ label: 'Source:', value: 'Trendsearch' }] : [],
      disabled: stockTickers.length === 0,
    },
    {
      id: 'news_trend',
      title: 'Your News Trend',
      icon: <AutoGraphIcon />,
      color: '#6a1b9a',
      stats: newsTrendStats,
      loading: tsLoading && newsTrendStats.length === 0,
      disabled: data.length === 0 && !tsLoading,
    },
    {
      id: 'alerts',
      title: 'Your Alerts',
      icon: <NotificationsActiveIcon />,
      color: '#c62828',
      stats: stockTickers.length > 0
        ? source === 'portfolio' && reduxAlerts.length > 0
          ? [{ label: 'Alerts:', value: `${reduxAlerts.filter(a => !a.bignore).length} active` }, { label: 'Tickers:', value: `${stockTickers.length} stocks` }]
          : [{ label: 'Scope:', value: 'Portfolio defs' }, { label: 'Tickers:', value: `${stockTickers.length} stocks` }]
        : [],
      disabled: stockTickers.length === 0,
    },
    {
      id: 'esg',
      title: 'Your ESG',
      icon: <EmojiNatureIcon />,
      color: '#388e3c',
      stats: esgStats,
      disabled: data.length === 0,
    },
    {
      id: 'momentum',
      title: 'Your Momentum',
      icon: <SpeedIcon />,
      color: '#f57c00',
      stats: momentumStats,
      loading: tpLoading && momentumStats.length === 0,
      disabled: Object.keys(tpData).length === 0 && !tpLoading,
      terminal: { hero: terminalHeroData.momentumHero },
    },
    {
      id: 'risk',
      title: 'Your Risk',
      icon: <SecurityIcon />,
      color: '#b71c1c',
      stats: riskStats,
      loading: (tpLoading || portfolioRiskLoading) && riskStats.length === 0,
      disabled: Object.keys(tpData).length === 0 && Object.keys(yfData).length === 0 && !portfolioRisk && !tpLoading && !portfolioRiskLoading,
      terminal: { hero: terminalHeroData.riskHero },
    },
    {
      id: 'performance',
      title: 'Your Performance',
      icon: <TrendingUpIcon />,
      color: '#1565c0',
      stats: performanceStats,
      loading: tpLoading && performanceStats.length === 0,
      disabled: Object.keys(tpData).length === 0 && !tpLoading,
      terminal: { hero: terminalHeroData.performanceHero },
    },
    {
      id: 'institutional_trend',
      title: 'Your Institutional Trend',
      icon: <BusinessIcon />,
      color: '#4a148c',
      stats: institutionalStats,
      loading: tpLoading && institutionalStats.length === 0,
      disabled: Object.keys(tpData).length === 0 && !tpLoading,
    },
    {
      id: 'cash',
      title: 'Cash Position',
      icon: <AccountBalanceWalletIcon />,
      color: '#607d8b',
      stats: [],
      disabled: true,
      terminalOnly: true,
      terminal: { hero: terminalHeroData.cashHero },
    },
  ];

  const activeCard = cards.find(c => c.id === openModal);

  // DAS-2185: Capture a single DOM element to a canvas
  const captureElement = useCallback(async (element) => {
    const prevOverflow = element.style.overflow;
    const prevMaxHeight = element.style.maxHeight;
    element.style.overflow = 'visible';
    element.style.maxHeight = 'none';

    const w = element.scrollWidth;
    const h = element.scrollHeight;

    const canvas = await html2canvas(element, {
      scale: 2, useCORS: true, logging: false,
      windowWidth: w, windowHeight: h, width: w, height: h,
      scrollX: 0, scrollY: 0,
    });

    element.style.overflow = prevOverflow;
    element.style.maxHeight = prevMaxHeight;
    return canvas;
  }, []);

  // Add a canvas image to the PDF, splitting across pages if needed
  const addCanvasToPdf = useCallback((pdf, canvas, startNewPage) => {
    const margin = 20;
    const pageWidth = pdf.internal.pageSize.getWidth() - 2 * margin;
    const pageHeight = pdf.internal.pageSize.getHeight() - 2 * margin;
    const ratio = pageWidth / canvas.width;
    const scaledHeight = canvas.height * ratio;
    const imgData = canvas.toDataURL('image/png');

    if (startNewPage) pdf.addPage();

    let yOffset = 0;
    pdf.addImage(imgData, 'PNG', margin, margin, pageWidth, scaledHeight);
    yOffset += pageHeight;

    while (yOffset < scaledHeight) {
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', margin, margin - yOffset, pageWidth, scaledHeight);
      yOffset += pageHeight;
    }
  }, []);

  // Generate PDF — each tab section gets its own page(s)
  const generatePdfFromRef = useCallback(async (ref) => {
    const element = ref?.current;
    if (!element) return null;

    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const sections = element.querySelectorAll('[data-pdf-section]');

    if (sections.length > 0) {
      for (let i = 0; i < sections.length; i++) {
        const canvas = await captureElement(sections[i]);
        addCanvasToPdf(pdf, canvas, i > 0);
      }
    } else {
      const canvas = await captureElement(element);
      addCanvasToPdf(pdf, canvas, false);
    }

    return pdf;
  }, [captureElement, addCanvasToPdf]);

  const handleDownloadPDF = useCallback(async () => {
    setPdfLoading(true);
    setPdfMode(true);
    // Wait for React to re-render all tab content and charts to paint
    await new Promise(r => setTimeout(r, 2000));

    try {
      const pdf = await generatePdfFromRef(modalContentRef);
      if (pdf) {
        const filename = `PA_${activeCard?.title?.replace(/\s+/g, '_') || 'Widget'}`;
        pdf.save(`${filename}.pdf`);
        message.success('PDF downloaded');
      } else {
        message.error('Could not capture content for PDF');
      }
    } catch (err) {
      console.error('PDF generation error:', err);
      message.error('Failed to generate PDF');
    } finally {
      setPdfMode(false);
      setPdfLoading(false);
    }
  }, [generatePdfFromRef, activeCard]);

  const openJiraModal = useCallback((context) => {
    setJiraContext(context || activeCard?.title || '');
    setJiraDescription('');
    setJiraModalOpen(true);
  }, [activeCard]);

  const handleCreateJiraTicket = useCallback(async () => {
    setJiraLoading(true);
    try {
      // Capture current modal content as PDF blob
      let pdfBlob = null;
      try {
        const pdf = await generatePdfFromRef(modalContentRef);
        if (pdf) pdfBlob = pdf.output('blob');
      } catch (pdfErr) {
        console.warn('PDF capture for Jira failed, creating ticket without attachment:', pdfErr);
      }

      const summary = `PA: ${jiraContext || activeCard?.title || 'Widget'} - User Report`;
      const description = [
        `## Portfolio Analysis – ${jiraContext || activeCard?.title || 'Widget'}`,
        '',
        jiraDescription || '(no additional comments)',
        '',
        `Source: ${source}`,
        `Generated: ${new Date().toISOString()}`,
      ].join('\n');

      const resp = await api.post('/jira/create_ticket', {
        summary,
        description,
        issue_type: 'Task',
        priority: 'Medium',
        labels: ['portfolio-analysis'],
      });

      if (!resp.data?.success || !resp.data?.key) {
        throw new Error(resp.data?.error || 'Failed to create ticket');
      }

      const issueKey = resp.data.key;

      if (pdfBlob) {
        const fd = new FormData();
        fd.append('issue_key', issueKey);
        fd.append('file', pdfBlob, `PA_${(jiraContext || activeCard?.title || 'Widget').replace(/\s+/g, '_')}.pdf`);
        try {
          await api.post('/jira/attach_file', fd);
        } catch (attachErr) {
          console.warn('PDF attachment failed:', attachErr);
          message.warning(`Ticket ${issueKey} created, but PDF attachment failed`);
        }
      }

      message.success(`Created Jira ticket: ${issueKey}`);
      setJiraModalOpen(false);
      setJiraDescription('');
      setJiraContext('');
    } catch (err) {
      console.error('Jira ticket creation error:', err);
      message.error(err.message || 'Failed to create Jira ticket');
    } finally {
      setJiraLoading(false);
    }
  }, [jiraContext, jiraDescription, activeCard, source, generatePdfFromRef]);

  const handleCardClick = (cardId) => {
    setOpenModal(cardId);
  };

  const renderModalContent = () => {
    switch (openModal) {
      case 'portfolio':
        return <PAModal_Portfolio data={data} source={source} />;
      case 'exposures':
        return <PAModal_Exposures exposures={exposures} source={source} yfData={yfData} data={data} pdfMode={pdfMode} userId={userId} onCreateJiraTicket={openJiraModal} />;
      case 'asset_allocation':
        return <PAModal_AssetAllocation data={data} source={source} etfStockPositions={etfStockPositions} etfLeftoverPositions={etfLeftoverPositions} dataProfile={dataProfile} fixedIncomeData={fixedIncomeData} yfData={yfData} pdfMode={pdfMode} userId={userId} onCreateJiraTicket={openJiraModal} />;
      case 'income':
        return <PAModal_Income data={data} source={source} depositInterestData={depositInterestData} yfData={yfData} exposures={exposures} />;
      case 'timeline':
        return <PAModal_Timeline yfData={yfData} divData={divData} depositInterestData={depositInterestData} data={data} source={source} />;
      case 'fundamentals':
        return <PAModal_Fundamentals tpData={tpData} yfData={yfData} data={data} source={source} />;
      case 'news':
        return <PAModal_News tickers={stockTickers} data={data} source={source} />;
      case 'news_trend':
        return <PAModal_NewsTrend data={data} source={source} exposures={exposures} tickerAggregates={tickerAggregates} sectorSentiments={sectorSentiments} />;
      case 'esg':
        return <PAModal_ESG tickers={stockTickers} data={data} source={source} />;
      case 'alerts':
        return <PAModal_Alerts tickers={stockTickers} data={data} source={source} reduxAlerts={reduxAlerts} />;
      case 'momentum':
        return <PAModal_Momentum tpData={tpData} data={data} source={source} pdfMode={pdfMode} userId={userId} onCreateJiraTicket={openJiraModal} />;
      case 'risk':
        return <PAModal_Risk tpData={tpData} yfData={yfData} data={data} source={source} portfolioRisk={portfolioRisk} portfolioRiskExposure={portfolioRiskExposure} exposures={exposures} />;
      case 'performance':
        return <PAModal_Performance tpData={tpData} yfData={yfData} data={data} source={source} benchmarkData={benchmarkData} />;
      case 'institutional_trend':
        return <PAModal_InstitutionalTrend tpData={tpData} yfData={yfData} data={data} source={source} />;
      default:
        return (
          <Box display="flex" alignItems="center" justifyContent="center" sx={{ minHeight: 300 }}>
            <Typography color="text.secondary">
              {activeCard?.title} — content coming soon
            </Typography>
          </Box>
        );
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('pa_viewMode', mode);
  };

  const alertCount = source === 'portfolio'
    ? reduxAlerts.filter(a => !a.bignore).length
    : signalAlerts.filter(a => !a.bignore).length;

  const gridCards = cards.filter(c => !c.terminalOnly);

  return (
    <TickerSignalContext.Provider value={tickerSignals}>
      {viewMode === 'terminal' ? (
        <TerminalLayout
          cards={cards}
          onCardClick={handleCardClick}
          onViewModeChange={handleViewModeChange}
          data={data}
          source={source}
          alertCount={alertCount}
          panelData={terminalPanelData}
          tpData={tpData}
          yfData={yfData}
          exposures={exposures}
          benchmarkData={benchmarkData}
          stockTickers={stockTickers}
          portfolioRisk={portfolioRisk}
        />
      ) : (
        <Box sx={{ p: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Box
              onClick={() => handleViewModeChange('terminal')}
              sx={{
                fontSize: 11,
                px: 1.5,
                py: 0.5,
                cursor: 'pointer',
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 0.5,
                fontFamily: 'monospace',
                '&:hover': { color: 'text.primary', borderColor: 'primary.main' },
              }}
            >
              terminal view
            </Box>
          </Box>
          <Grid container spacing={2}>
            {gridCards.map((card) => (
              <Grid item xs={6} sm={6} md={4} lg={3} key={card.id} data-testid={`analysis-card-${card.id}`}>
                <AnalysisCard
                  icon={card.icon}
                  title={card.title}
                  stats={card.stats}
                  color={card.color}
                  loading={card.loading}
                  onClick={() => handleCardClick(card.id)}
                  disabled={card.disabled}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Shared modal — works for both views */}
      <Dialog
        open={!!openModal}
        onClose={() => setOpenModal(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '60vh' } }}
      >
        {activeCard && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 14 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  bgcolor: `${activeCard.color}14`,
                  color: activeCard.color,
                }}
              >
                {activeCard.icon}
              </Box>
              {activeCard.title}
              {/* DAS-2185: PDF download & Jira ticket buttons */}
              <Box sx={{ position: 'absolute', right: 48, top: 8, display: 'flex', gap: 0.5 }}>
                <Tooltip title="Download PDF">
                  <span>
                    <IconButton onClick={handleDownloadPDF} disabled={pdfLoading} size="small">
                      {pdfLoading ? <CircularProgress size={18} /> : <PictureAsPdfIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                {userId === 2 && (
                  <Tooltip title="Create Jira Ticket">
                    <IconButton onClick={() => openJiraModal()} size="small">
                      <ConfirmationNumberIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <IconButton
                onClick={() => setOpenModal(null)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers ref={modalContentRef}>
              {renderModalContent()}
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* DAS-2185: Jira Ticket Creation Modal */}
      <Dialog open={jiraModalOpen} onClose={() => setJiraModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Jira Ticket</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            A new Jira ticket will be created for <strong>{jiraContext || 'this widget'}</strong> with a PDF snapshot attached.
          </Typography>
          <TextField
            label="Comments / Requirements"
            placeholder="Describe what you'd like to report or request..."
            multiline
            rows={4}
            fullWidth
            value={jiraDescription}
            onChange={(e) => setJiraDescription(e.target.value)}
            inputProps={{ maxLength: 1000 }}
            helperText={`${jiraDescription.length}/1000`}
          />
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Included in ticket:</strong>
              <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                <li>Widget: {jiraContext || activeCard?.title || '—'}</li>
                <li>PDF snapshot of current view</li>
                <li>Your comments above</li>
              </ul>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setJiraModalOpen(false)}>Cancel</MuiButton>
          <MuiButton
            variant="contained"
            onClick={handleCreateJiraTicket}
            disabled={jiraLoading}
            startIcon={jiraLoading ? <CircularProgress size={16} /> : <ConfirmationNumberIcon />}
          >
            {jiraLoading ? 'Creating...' : 'Create Ticket'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </TickerSignalContext.Provider>
  );
};

export default PortfolioAnalysisView;
